/**
 * Cent-safe invoice settlement math.
 *
 * Amounts from Postgres numeric columns arrive as strings. All sums stay in
 * integer cents until a single display conversion. The database remains the
 * authority for whether a receipt is allowed.
 */

export const PAYMENT_STATE = Object.freeze({
  UNPAID: "unpaid",
  PARTIAL: "partial",
  PAID: "paid",
});

export function toCents(value) {
  if (value == null || value === "") return 0;

  if (typeof value === "number") {
    if (!Number.isFinite(value)) {
      throw new Error("Enter a valid payment amount.");
    }
    return Math.round(value * 100 + (value >= 0 ? Number.EPSILON : -Number.EPSILON));
  }

  const raw = String(value).trim().replace(/,/g, "");
  const match = raw.match(/^(-?)(\d+)(?:\.(\d+))?$/);
  if (!match) {
    throw new Error("Enter a valid payment amount.");
  }

  const sign = match[1] === "-" ? -1 : 1;
  const whole = Number(match[2]);
  const fraction = match[3] || "";
  if (fraction.length > 2) {
    throw new Error("Enter the amount in dollars and cents.");
  }
  const cents = Number((fraction + "00").slice(0, 2));
  return sign * (whole * 100 + cents);
}

export function centsToDecimal(cents) {
  const sign = cents < 0 ? "-" : "";
  const abs = Math.abs(cents);
  const whole = Math.floor(abs / 100);
  const fraction = String(abs % 100).padStart(2, "0");
  return `${sign}${whole}.${fraction}`;
}

export function formatMoneyFromCents(cents) {
  const sign = cents < 0 ? "-" : "";
  const abs = Math.abs(cents);
  const whole = Math.floor(abs / 100).toLocaleString("en-CA");
  const fraction = String(abs % 100).padStart(2, "0");
  return `${sign}$${whole}.${fraction}`;
}

export function isReversalPayment(payment) {
  return (
    payment?.payment_role === "reversal" ||
    Boolean(payment?.reversal_of_payment_id)
  );
}

export function reversedPaymentIds(payments = []) {
  const ids = new Set();
  for (const payment of payments || []) {
    if (payment?.reversal_of_payment_id) {
      ids.add(payment.reversal_of_payment_id);
    }
  }
  return ids;
}

export function unreversedReceipts(payments = []) {
  const reversed = reversedPaymentIds(payments);
  return (payments || []).filter(
    (payment) => !isReversalPayment(payment) && !reversed.has(payment?.payment_id),
  );
}

export function sumUnreversedReceiptCents(payments = []) {
  return unreversedReceipts(payments).reduce(
    (sum, payment) => sum + toCents(payment?.amount),
    0,
  );
}

/**
 * Outstanding cents for one invoice.
 * When `invoice_payments` is absent, paid invoices contribute 0 and issued
 * invoices contribute their total. Loaders that embed the ledger always pass
 * an array, including an empty one.
 */
const ISSUED_INVOICE_STATUSES = new Set(["sent", "paid"]);

/**
 * An open invoice is an issued invoice with a remaining ledger balance.
 * Draft and archived invoices are not open. A sent invoice whose receipts
 * have cleared the balance is not open.
 */
export function isOpenReceivableInvoice(invoice) {
  if (!ISSUED_INVOICE_STATUSES.has(invoice?.status)) return false;
  return outstandingCents(invoice) > 0;
}

export function outstandingCents(invoice) {
  if (!invoice) return 0;
  const total = toCents(invoice.total_amount);
  const payments = invoice.invoice_payments;
  if (!Array.isArray(payments)) {
    const status = invoice.status;
    if (status === "paid") return 0;
    if (status === "sent") return Math.max(total, 0);
    return 0;
  }
  const paid = sumUnreversedReceiptCents(payments);
  return Math.max(total - paid, 0);
}

export function paidCents(invoice) {
  const payments = invoice?.invoice_payments;
  if (!Array.isArray(payments)) {
    if (invoice?.status === "paid") return toCents(invoice.total_amount);
    return 0;
  }
  return sumUnreversedReceiptCents(payments);
}

export function derivePaymentState(invoice) {
  const paid = paidCents(invoice);
  const outstanding = outstandingCents(invoice);
  if (paid <= 0) {
    return PAYMENT_STATE.UNPAID;
  }
  if (outstanding <= 0) return PAYMENT_STATE.PAID;
  return PAYMENT_STATE.PARTIAL;
}

export function paymentStateLabel(state) {
  if (state === PAYMENT_STATE.PARTIAL) return "Partially Paid";
  if (state === PAYMENT_STATE.PAID) return "Paid";
  return "Unpaid";
}

export function formatSettlementLabel(state, overdue = false) {
  const label = paymentStateLabel(state);
  return overdue ? `${label} · Overdue` : label;
}

export function assertAmountWithinRemaining(totalCents, paidCentsValue, nextCents) {
  if (!Number.isInteger(nextCents) || nextCents <= 0) {
    throw new Error("Enter a payment amount greater than zero.");
  }
  const remaining = totalCents - paidCentsValue;
  if (nextCents > remaining) {
    throw new Error("Payment exceeds the remaining balance.");
  }
  return remaining - nextCents;
}

export function summarizeInvoiceSettlement(invoice) {
  const total = toCents(invoice?.total_amount);
  const payments = Array.isArray(invoice?.invoice_payments)
    ? invoice.invoice_payments
    : [];
  const paid = Array.isArray(invoice?.invoice_payments)
    ? sumUnreversedReceiptCents(payments)
    : paidCents(invoice);
  const outstanding = Array.isArray(invoice?.invoice_payments)
    ? Math.max(total - paid, 0)
    : outstandingCents(invoice);
  const state = Array.isArray(invoice?.invoice_payments)
    ? paid <= 0
      ? PAYMENT_STATE.UNPAID
      : outstanding <= 0
        ? PAYMENT_STATE.PAID
        : PAYMENT_STATE.PARTIAL
    : derivePaymentState(invoice);

  return {
    totalCents: total,
    paidCents: paid,
    outstandingCents: outstanding,
    state,
    hasUnreversedPayment: unreversedReceipts(payments).length > 0,
    payments,
  };
}
